import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { testEvaluationService } from '../services/testEvaluationService';
import {
  TestResult,
  TestStatistics,
  GetTestResultsParams,
  AddCommentRequest,
  UpdateCommentRequest,
} from '../types/testEvaluation';

interface UseTestEvaluationState {
  results: TestResult[];
  statistics: TestStatistics | null;
  loading: boolean;
  error: string | null;
  continuationToken: string | null;
}

interface UseTestEvaluationReturn extends UseTestEvaluationState {
  fetchResults: (params?: GetTestResultsParams) => Promise<void>;
  fetchStatistics: (params?: { agentId?: string; startDate?: string; endDate?: string; category?: string; passed?: boolean }) => Promise<void>;
  loadMore: () => Promise<void>;
  addComment: (testResultId: string, comment: string) => Promise<void>;
  updateComment: (testResultId: string, commentId: string, comment: string) => Promise<void>;
  deleteComment: (testResultId: string, commentId: string) => Promise<void>;
  clearError: () => void;
  hasAccess: boolean;
}

/**
 * Custom hook for managing test evaluation data
 * Requires testContributor role for access
 */
export const useTestEvaluation = (initialParams?: GetTestResultsParams): UseTestEvaluationReturn => {
  const { isAuthenticated, user } = useAuth();
  
  const [state, setState] = useState<UseTestEvaluationState>({
    results: [],
    statistics: null,
    loading: false,
    error: null,
    continuationToken: null,
  });

  const [lastParams, setLastParams] = useState<GetTestResultsParams | undefined>(initialParams);

  // Check if user has testContributor role
  const hasAccess = Boolean(isAuthenticated && user?.roles?.includes('testContributor'));

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Fetch test results
  const fetchResults = useCallback(async (params?: GetTestResultsParams) => {
    if (!hasAccess) {
      setState(prev => ({ 
        ...prev, 
        error: 'Access denied. testContributor role required.' 
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await testEvaluationService.getTestResults(params);
      
      setState(prev => ({
        ...prev,
        results: response.results,
        continuationToken: response.continuationToken || null,
        loading: false,
      }));

      setLastParams(params);
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch test results',
      }));
    }
  }, [hasAccess]);

  // Load more results (pagination)
  const loadMore = useCallback(async () => {
    if (!state.continuationToken || state.loading || !hasAccess) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await testEvaluationService.getTestResults({
        ...lastParams,
        continuationToken: state.continuationToken,
      });

      setState(prev => ({
        ...prev,
        results: [...prev.results, ...response.results],
        continuationToken: response.continuationToken || null,
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load more results',
      }));
    }
  }, [state.continuationToken, state.loading, hasAccess, lastParams]);

  // Fetch statistics
  const fetchStatistics = useCallback(async (params?: { 
    agentId?: string; 
    startDate?: string; 
    endDate?: string 
  }) => {
    if (!hasAccess) {
      setState(prev => ({ 
        ...prev, 
        error: 'Access denied. testContributor role required.' 
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const statistics = await testEvaluationService.getStatistics(params);
      
      setState(prev => ({
        ...prev,
        statistics,
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch statistics',
      }));
    }
  }, [hasAccess]);

  // Add comment to test result
  const addComment = useCallback(async (testResultId: string, comment: string) => {
    if (!hasAccess) {
      setState(prev => ({ 
        ...prev, 
        error: 'Access denied. testContributor role required.' 
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const request: AddCommentRequest = { comment };
      const response = await testEvaluationService.addComment(testResultId, request);

      // Update the specific test result in state
      setState(prev => ({
        ...prev,
        results: prev.results.map(result => 
          result.id === testResultId
            ? { 
                ...result, 
                reviewerComments: [...(result.reviewerComments || []), response.comment] 
              }
            : result
        ),
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to add comment',
      }));
    }
  }, [hasAccess]);

  // Update comment
  const updateComment = useCallback(async (
    testResultId: string, 
    commentId: string, 
    comment: string
  ) => {
    if (!hasAccess) {
      setState(prev => ({ 
        ...prev, 
        error: 'Access denied. testContributor role required.' 
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const request: UpdateCommentRequest = { comment };
      const response = await testEvaluationService.updateComment(testResultId, commentId, request);

      // Update the specific comment in state
      setState(prev => ({
        ...prev,
        results: prev.results.map(result => 
          result.id === testResultId
            ? {
                ...result,
                reviewerComments: (result.reviewerComments || []).map(c =>
                  c.id === commentId ? response.comment : c
                ),
              }
            : result
        ),
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to update comment',
      }));
    }
  }, [hasAccess]);

  // Delete comment
  const deleteComment = useCallback(async (
    testResultId: string, 
    commentId: string
  ) => {
    if (!hasAccess) {
      setState(prev => ({ 
        ...prev, 
        error: 'Access denied. testContributor role required.' 
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      await testEvaluationService.deleteComment(testResultId, commentId);

      // Remove the comment from state
      setState(prev => ({
        ...prev,
        results: prev.results.map(result => 
          result.id === testResultId
            ? {
                ...result,
                reviewerComments: (result.reviewerComments || []).filter(c => c.id !== commentId),
              }
            : result
        ),
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to delete comment',
      }));
    }
  }, [hasAccess]);

  // Auto-fetch on mount if user has access and initial params provided
  useEffect(() => {
    if (hasAccess && initialParams) {
      fetchResults(initialParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]); // Only run on hasAccess change to avoid infinite loops

  return {
    ...state,
    fetchResults,
    fetchStatistics,
    loadMore,
    addComment,
    updateComment,
    deleteComment,
    clearError,
    hasAccess,
  };
};
